import { useRef } from "react";
import { motion, useMotionValue, useSpring, useTransform } from "framer-motion";

/**
 * Magnetic button — element leans toward the cursor when hovered.
 * Renders a semantic <button> so a11y is preserved.
 */
export default function MagneticButton({
    as: As = "button",
    strength = 0.35,
    className = "",
    children,
    ...rest
}) {
    const ref = useRef(null);
    const x = useMotionValue(0);
    const y = useMotionValue(0);
    const sx = useSpring(x, { stiffness: 180, damping: 15, mass: 0.4 });
    const sy = useSpring(y, { stiffness: 180, damping: 15, mass: 0.4 });
    const rx = useTransform(sy, (v) => v * -0.05);
    const ry = useTransform(sx, (v) => v * 0.05);

    const onMove = (e) => {
        const el = ref.current;
        if (!el) return;
        const r = el.getBoundingClientRect();
        const dx = e.clientX - (r.left + r.width / 2);
        const dy = e.clientY - (r.top + r.height / 2);
        x.set(dx * strength);
        y.set(dy * strength);
    };
    const onLeave = () => {
        x.set(0);
        y.set(0);
    };

    return (
        <motion.div
            ref={ref}
            onMouseMove={onMove}
            onMouseLeave={onLeave}
            style={{ x: sx, y: sy, rotateX: rx, rotateY: ry, transformStyle: "preserve-3d" }}
            className="inline-block"
        >
            <As className={className} data-cursor="hover" {...rest}>
                {children}
            </As>
        </motion.div>
    );
}
